import { useMemo, useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { MaxItemsState } from "@nens/ranalib";
import { useDebounce } from "@nens/react/hooks";
import { combineExtents } from "@nens/shared-utils";

import { FIT_BOUNDS_OPTIONS } from "@/lib/rana-map";
import { usePmTilesFeaturesQuery } from "@/queries/query-pmtiles-features";

import { FeatureSelectorFieldProps } from "../types/feature-selector-field-props-type";
import { getFeaturePlaceName } from "../utils/get-feature-place-name";

export interface ProcessRunFormFileFeatureSelectorSearchProps {
  pmTilesUrl: string;
  layerName: string;
  bounds: [number, number, number, number] | null;
  fieldProps: FeatureSelectorFieldProps;
}

const DEFAULT_MAX_ITEMS = 5;

export const useFeatureSelectSearch = ({
  pmTilesUrl,
  layerName,
  bounds,
  fieldProps,
}: ProcessRunFormFileFeatureSelectorSearchProps) => {
  const { formData, onChange } = fieldProps;
  const map = useMap().current;
  const [searchString, setSearchString] = useState("");
  const debouncedSearchString = useDebounce(searchString, 250);
  const [maxItems, setMaxItems] = useState(DEFAULT_MAX_ITEMS);
  const maxItemsState: MaxItemsState = {
    maxItems,
    initialMaxItems: DEFAULT_MAX_ITEMS,
    increaseMaxItems: () => {
      setMaxItems((count) => count + DEFAULT_MAX_ITEMS);
    },
    resetMaxItems: () => {
      setMaxItems(DEFAULT_MAX_ITEMS);
    },
  };

  const { data, isLoading } = usePmTilesFeaturesQuery(pmTilesUrl);

  const features = useMemo(() => {
    if (!data) return data;
    return [...data].sort((a, b) => {
      const nameA = getFeaturePlaceName(a);
      const nameB = getFeaturePlaceName(b);
      if (!nameA) return 1;
      if (!nameB) return -1;
      return nameA.localeCompare(nameB, undefined, {
        sensitivity: "base",
        ignorePunctuation: true,
      });
    });
  }, [data]);

  const filteredPlaceNames = useMemo(() => {
    if (!features) return [];
    const filteredPlaceNames: string[] = [];

    for (const feature of features) {
      if (filteredPlaceNames.length >= maxItems) break;
      const placeName = getFeaturePlaceName(feature);
      if (!placeName) continue;
      if (
        !filteredPlaceNames.includes(placeName) &&
        (!debouncedSearchString ||
          placeName.toLowerCase().includes(debouncedSearchString.toLowerCase()))
      ) {
        filteredPlaceNames.push(placeName);
      }
    }
    return filteredPlaceNames;
  }, [debouncedSearchString, features, maxItems]);

  const selectedPlaceNames = useMemo(() => {
    if (!formData?.layer_name || !features) return [];

    const selectedPlaceNames: string[] = [];
    features.forEach((feature) => {
      const selectedFeatureIds = formData.feature_ids;
      const isAMatch = selectedFeatureIds.includes(feature.id);
      const placeName = getFeaturePlaceName(feature);
      if (isAMatch && placeName && !selectedPlaceNames.includes(placeName)) {
        selectedPlaceNames.push(placeName);
      }
    });

    return selectedPlaceNames;
  }, [features, formData]);

  const onSelectChange = (placeName: string | undefined) => {
    if (!map || !features) return;
    if (placeName === undefined) {
      onChange(undefined);
      if (bounds) map?.fitBounds(bounds, { ...FIT_BOUNDS_OPTIONS, duration: 1500 });
      return;
    }
    const filteredFeatures = features.filter((f) => getFeaturePlaceName(f) === placeName);
    if (!filteredFeatures.length) return;
    const filteredFeatureBounds = combineExtents(filteredFeatures.map((f) => f.bounds));
    if (filteredFeatureBounds)
      map?.fitBounds(filteredFeatureBounds, { ...FIT_BOUNDS_OPTIONS, duration: 1500 });
    onChange({
      layer_name: layerName,
      feature_names: filteredFeatures.map((f) => getFeaturePlaceName(f) ?? ""),
      feature_ids: filteredFeatures.map((f) => f.id),
    });
  };

  return {
    isLoading,
    searchString,
    setSearchString,
    filteredPlaceNames,
    selectedPlaceNames,
    onSelectChange,
    maxItemsState,
  } as const;
};
